import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

const STORAGE_KEY = 'appLanguage';
const STORAGE_DIRECTION_KEY = 'appDirection';
const STORAGE_BOTTOM_NAVBAR_DIRECTION_KEY = 'appBottomNavBarDirection';
const DEFAULT_LANGUAGE = 'ar';
const DEFAULT_DIRECTION = 'auto';
const DEFAULT_BOTTOM_NAVBAR_DIRECTION = 'auto';
const SUPPORTED_LANGUAGES = ['ar', 'en'];
const SUPPORTED_DIRECTIONS = ['auto', 'rtl', 'ltr'];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
  });

  const [direction, setDirectionState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_DIRECTION;
    const stored = window.localStorage.getItem(STORAGE_DIRECTION_KEY);
    return SUPPORTED_DIRECTIONS.includes(stored) ? stored : DEFAULT_DIRECTION;
  });

  const [bottomNavBarDirection, setBottomNavBarDirectionState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_BOTTOM_NAVBAR_DIRECTION;
    const stored = window.localStorage.getItem(STORAGE_BOTTOM_NAVBAR_DIRECTION_KEY);
    return SUPPORTED_DIRECTIONS.includes(stored) ? stored : DEFAULT_BOTTOM_NAVBAR_DIRECTION;
  });

  const resolvedDirection = direction === 'auto' ? (language === 'ar' ? 'rtl' : 'ltr') : direction;
  const resolvedBottomNavBarDirection = bottomNavBarDirection === 'auto' ? resolvedDirection : bottomNavBarDirection;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, language);
    window.localStorage.setItem(STORAGE_DIRECTION_KEY, direction);
    window.localStorage.setItem(STORAGE_BOTTOM_NAVBAR_DIRECTION_KEY, bottomNavBarDirection);
    document.documentElement.lang = language === 'ar' ? 'ar' : 'en';
    document.documentElement.dir = resolvedDirection;
  }, [language, direction, bottomNavBarDirection, resolvedDirection]);

  const setLanguage = (lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    setLanguageState(lang);
  };

  const setDirection = (dir) => {
    if (!SUPPORTED_DIRECTIONS.includes(dir)) return;
    setDirectionState(dir);
  };

  const setBottomNavBarDirection = (dir) => {
    if (!SUPPORTED_DIRECTIONS.includes(dir)) return;
    setBottomNavBarDirectionState(dir);
  };

  const t = (key, paramsOrFallback = {}, fallback) => {
    const params = typeof paramsOrFallback === 'string' || paramsOrFallback == null ? {} : paramsOrFallback;
    const effectiveFallback = typeof paramsOrFallback === 'string' ? paramsOrFallback : fallback;
    const languageMap = translations[language] || {};
    const englishFallback = translations.en || {};
    let value = languageMap[key] ?? englishFallback[key] ?? effectiveFallback ?? key;
    if (typeof value !== 'string') return value;
    return Object.keys(params).reduce((str, paramKey) => {
      const replacement = params[paramKey];
      return str.split(`{${paramKey}}`).join(replacement);
    }, value);
  };

  const value = useMemo(() => ({
    language,
    setLanguage,
    direction,
    setDirection,
    bottomNavBarDirection,
    setBottomNavBarDirection,
    resolvedDirection,
    resolvedBottomNavBarDirection,
    t
  }), [language, direction, bottomNavBarDirection, resolvedDirection, resolvedBottomNavBarDirection]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
